import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  ArrowLeftIcon,
  Button,
  ErrorMessage,
  Field,
  FormLabel,
  Heading,
  Input,
  Label,
  Popup,
  Select,
  cn,
  showToast,
} from '@quickmd-llc/design-system'
import { MEMBER_PAGE_ROUTE } from '@/utils/constants'
import { statesOptions } from '@/utils/common'
import type { DoctorLicenseAdminApiOutContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import type { LicenseFormValues, LicenseFormVisitType, LicenseEntryType, ServiceType } from './LicenseFormSchema'
import { licenseFormSchema } from './LicenseFormSchema'
import { flattenServiceTypes } from './flattenServiceTypes'
import { visibilityTypes } from '../Constants'

const STATE_SELECT_OPTIONS = statesOptions.map((s: string) => {
  const code = s.slice(-2)
  return { label: s, value: code }
})

const VISIBILITY_OPTIONS = [
  { label: visibilityTypes.noBookings.displayName, value: visibilityTypes.noBookings.name },
  { label: visibilityTypes.repeatPatients.displayName, value: visibilityTypes.repeatPatients.name },
  { label: visibilityTypes.allPatients.displayName, value: visibilityTypes.allPatients.name },
]

const LICENSE_TYPE_OPTIONS: { label: string; value: LicenseEntryType }[] = [
  { label: 'Medical', value: 'Medical' },
  { label: 'CSL', value: 'CSL' },
  { label: 'DEA', value: 'DEA' },
]

const MAX_LICENSE_ENTRIES = 3

const DEFAULT_VALUES: LicenseFormValues = {
  stateCode: '',
  licenseEntries: [{ type: 'Medical', number: '', deaStateCode: '' }],
  visitTypes: [],
}

function getBulkVisibility(visitTypes: LicenseFormVisitType[]): string {
  const selected = visitTypes.filter((vt) => vt.isSelected)
  if (selected.length === 0) return 'all'
  const first = selected[0]?.visibilityOverride
  const allSame = selected.every((vt) => vt.visibilityOverride === first)
  return allSame ? (first ?? 'all') : 'custom'
}

// --- Pill Button Group ---

interface VisibilityButtonGroupProps {
  value: string
  onChange: (value: string) => void
  showCustom?: boolean
}

function VisibilityButtonGroup({ value, onChange, showCustom }: VisibilityButtonGroupProps) {
  const options = [...(showCustom ? [{ label: 'Custom', value: 'custom' }] : []), ...VISIBILITY_OPTIONS]

  return (
    <div className='flex gap-sp-1'>
      {options.map((opt) => {
        const isActive = value === opt.value
        const isCustom = opt.value === 'custom'
        return (
          <button
            key={opt.value}
            type='button'
            onClick={() => {
              if (!isCustom) onChange(opt.value)
            }}
            className={cn(
              'flex min-w-[80px] items-center justify-center gap-[2px] rounded-full border px-sp-1 py-[3px] text-xs font-semibold tracking-[0.12px]',
              isActive ?
                'border-[#178199] bg-[#178199] text-white'
              : 'border-shade-10 bg-[#e8f2f5] text-text-highlight',
              isCustom ? 'cursor-default' : 'cursor-pointer',
            )}>
            {isActive && !isCustom ?
              <svg width='12' height='12' viewBox='0 0 12 12' fill='currentColor'>
                <path d='M10 3.5a.5.5 0 0 1 0 .7l-4.5 4.5a.5.5 0 0 1-.7 0L2.5 6.4a.5.5 0 1 1 .7-.7L5 7.4l4.1-4.1a.5.5 0 0 1 .7 0z' />
              </svg>
            : null}
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

// --- Main Form ---

interface LicenseFormPageProps {
  mode: 'add' | 'edit'
  defaultValues?: Partial<LicenseFormValues>
  availableServiceTypes?: ServiceType[]
  existingLicenses?: DoctorLicenseAdminApiOutContract[]
  currentLicenseId?: string
  successMessage: string
  onSubmit: (values: LicenseFormValues) => Promise<void>
  isPending: boolean
  /** Override the default Back/Cancel destination. Used by the new-member create flow,
   *  which closes a fullscreen overlay instead of routing back to the existing-member page. */
  onNavigateBack?: () => void
  /** When provided, called after a successful submit instead of the default navigate-back.
   *  Lets a parent close its overlay and stay on the same page. */
  onAfterSuccess?: () => void
}

export function LicenseFormPage({
  mode,
  defaultValues,
  availableServiceTypes = [],
  existingLicenses = [],
  currentLicenseId,
  successMessage,
  onSubmit,
  isPending,
  onNavigateBack,
  onAfterSuccess,
}: LicenseFormPageProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const userId = searchParams.get('id') ?? ''

  const flatVisitTypes = useMemo(() => flattenServiceTypes(availableServiceTypes), [availableServiceTypes])

  const {
    register,
    control,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<LicenseFormValues>({
    resolver: zodResolver(licenseFormSchema),
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
      visitTypes: defaultValues?.visitTypes ?? flatVisitTypes,
    },
    mode: 'onChange',
  })

  // Sync visitTypes when service types load asynchronously
  useEffect(() => {
    if (flatVisitTypes.length > 0 && !defaultValues?.visitTypes) {
      reset((prev) => ({ ...prev, visitTypes: flatVisitTypes }), { keepDirty: true })
    }
  }, [flatVisitTypes, reset, defaultValues?.visitTypes])

  const {
    fields: licenseFields,
    append: appendEntry,
    remove: removeEntry,
  } = useFieldArray({
    control,
    name: 'licenseEntries',
  })

  const watchedEntries = watch('licenseEntries')
  const [showDiscardPopup, setShowDiscardPopup] = useState(false)

  // Gap 6: Warn on browser close/refresh with unsaved changes
  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  const backUrl = `${MEMBER_PAGE_ROUTE}?id=${userId}&type=Provider&tab=${encodeURIComponent('Licenses & Productivity')}`

  function navigateBack() {
    if (onNavigateBack) {
      onNavigateBack()
      return
    }
    navigate(backUrl)
  }

  /** Gap 6: Check for unsaved changes before navigating away */
  function handleNavigateBack() {
    if (isDirty) {
      setShowDiscardPopup(true)
    } else {
      navigateBack()
    }
  }

  async function handleFormSubmit(values: LicenseFormValues) {
    // Gap 5: Skip no-op submissions on edit
    if (mode === 'edit' && !isDirty) {
      navigateBack()
      return
    }

    // Gap 3: Client-side duplicate check (medical license number)
    const medicalEntry = values.licenseEntries.find((e) => e.type === 'Medical')
    const medicalNumber = medicalEntry?.number ?? ''
    const isDuplicate = existingLicenses.some(
      (l) =>
        l?.StateCode === values.stateCode &&
        (l.Number ?? '').toUpperCase() === medicalNumber.toUpperCase() &&
        (mode === 'add' || l.LicenseId !== currentLicenseId),
    )
    if (isDuplicate) {
      setError('stateCode', { message: 'A license with this state and number already exists' })
      const medIdx = values.licenseEntries.findIndex((e) => e.type === 'Medical')
      if (medIdx >= 0) {
        setError(`licenseEntries.${medIdx}.number`, { message: 'Duplicate license' })
      }
      return
    }

    // Gap 7: API error handling
    try {
      await onSubmit(values)
      showToast({ type: 'success', message: successMessage, position: 'bottom-right' })
      if (onAfterSuccess) {
        onAfterSuccess()
      } else {
        navigateBack()
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to save license'
      showToast({ type: 'error', message: msg, position: 'bottom-right' })
    }
  }

  const title = mode === 'add' ? 'Add license' : 'Edit license'

  return (
    <div className='w-full'>
      <form onSubmit={handleSubmit(handleFormSubmit)} className='grid h-lvh [grid-template-rows:1fr_auto]'>
        {/* Scrollable content */}
        <div className='overflow-y-auto bg-background p-sp-6'>
          <div>
            <button
              type='button'
              onClick={handleNavigateBack}
              className='mb-sp-0.5 flex items-center gap-sp-0.5 text-text-dark'>
              <ArrowLeftIcon iconSize='s' />
              <Label size='md'>Back</Label>
            </button>
            <Heading level='h1'>{title}</Heading>
          </div>

          <div className='mt-sp-3 flex flex-col gap-sp-3'>
            {/* License Information Section */}
            <div className='rounded-lg border border-border-medium bg-white'>
              <div className='border-b border-border-medium px-sp-3 py-sp-2'>
                <Heading level='h3'>License information</Heading>
              </div>
              <div className='flex flex-col gap-sp-3 p-sp-3'>
                {/* State license — half width */}
                <div className='w-1/2 pr-sp-1.5'>
                  <Field error={!!errors.stateCode}>
                    <FormLabel>State license</FormLabel>
                    {errors.stateCode ?
                      <ErrorMessage>{errors.stateCode.message}</ErrorMessage>
                    : null}
                    <Select
                      {...register('stateCode', {
                        onChange: () => clearErrors('stateCode'),
                      })}
                      placeholder='Select a state'
                      error={!!errors.stateCode}>
                      {STATE_SELECT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>

                {/* License entries */}
                <div className='flex flex-col gap-sp-2'>
                  {licenseFields.map((field, idx) => {
                    const entryErrors = errors.licenseEntries?.[idx]
                    const isFirstMedical = idx === 0 && watchedEntries[0]?.type === 'Medical'
                    const currentType = watchedEntries[idx]?.type
                    const usedTypes = watchedEntries.map((e) => e.type)

                    return (
                      <div key={field.id} className='flex items-end gap-sp-2'>
                        {/* License type dropdown */}
                        <Field className='flex-1'>
                          <FormLabel>License type</FormLabel>
                          <Select
                            {...register(`licenseEntries.${idx}.type`)}
                            disabled={isFirstMedical}
                            placeholder='Select an option'>
                            {LICENSE_TYPE_OPTIONS.map((opt) => {
                              const taken = usedTypes.includes(opt.value) && opt.value !== currentType
                              return (
                                <option key={opt.value} value={opt.value} disabled={taken}>
                                  {opt.label}
                                </option>
                              )
                            })}
                          </Select>
                        </Field>

                        {/* License number input */}
                        <Field error={!!entryErrors?.number} className='flex-1'>
                          <FormLabel>License no.</FormLabel>
                          {entryErrors?.number ?
                            <ErrorMessage>{entryErrors.number.message}</ErrorMessage>
                          : null}
                          <Controller
                            control={control}
                            name={`licenseEntries.${idx}.number`}
                            render={({ field }) => (
                              <Input
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                  field.onChange(e.target.value.toUpperCase())
                                  clearErrors(`licenseEntries.${idx}.number`)
                                }}
                                placeholder='Enter license number'
                                error={!!entryErrors?.number}
                              />
                            )}
                          />
                        </Field>

                        {/* DEA state dropdown — only for DEA type */}
                        {currentType === 'DEA' && (
                          <Field error={!!entryErrors?.deaStateCode} className='flex-1'>
                            <FormLabel>DEA state</FormLabel>
                            {entryErrors?.deaStateCode ?
                              <ErrorMessage>{entryErrors.deaStateCode.message}</ErrorMessage>
                            : null}
                            <Select
                              {...register(`licenseEntries.${idx}.deaStateCode`)}
                              placeholder='Select state'
                              error={!!entryErrors?.deaStateCode}>
                              {STATE_SELECT_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </Select>
                          </Field>
                        )}

                        {/* Trash icon — not shown for first Medical row */}
                        {!isFirstMedical ?
                          <button
                            type='button'
                            onClick={() => removeEntry(idx)}
                            aria-label='Remove license entry'
                            className='flex size-[44px] shrink-0 items-center justify-center text-text-light hover:text-text-error'>
                            <svg width='18' height='18' viewBox='0 0 448 512' fill='currentColor'>
                              <path d='M135.2 17.7C140.6 6.8 151.7 0 163.8 0h120.4c12.1 0 23.2 6.8 28.6 17.7L320 32h80c17.7 0 32 14.3 32 32s-14.3 32-32 32H48C30.3 96 16 81.7 16 64s14.3-32 32-32h80l7.2-14.3zM32 128h384v320c0 35.3-28.7 64-64 64H96c-35.3 0-64-28.7-64-64V128zm96 64c-8.8 0-16 7.2-16 16v224c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16v224c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16v224c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16z' />
                            </svg>
                          </button>
                        : <div className='size-[44px] shrink-0' />}
                      </div>
                    )
                  })}

                  {/* Add button — hidden when max rows reached */}
                  {licenseFields.length < MAX_LICENSE_ENTRIES && (
                    <button
                      type='button'
                      onClick={() => {
                        const usedTypes = watchedEntries.map((e) => e.type)
                        const nextType =
                          (['CSL', 'DEA'] satisfies LicenseEntryType[]).find((t) => !usedTypes.includes(t)) ?? 'CSL'
                        appendEntry({ type: nextType, number: '', deaStateCode: '' })
                      }}
                      className='mt-sp-1 flex w-fit items-center gap-sp-0.5 text-sm font-semibold text-[#178199]'>
                      <svg width='14' height='14' viewBox='0 0 14 14' fill='currentColor'>
                        <path d='M7 1a.75.75 0 0 1 .75.75v4.5h4.5a.75.75 0 0 1 0 1.5h-4.5v4.5a.75.75 0 0 1-1.5 0v-4.5h-4.5a.75.75 0 0 1 0-1.5h4.5v-4.5A.75.75 0 0 1 7 1z' />
                      </svg>
                      Add
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Booking Access Settings Section */}
            <Controller
              name='visitTypes'
              control={control}
              render={({ field }) => {
                const hasSelected = field.value.some((vt) => vt.isSelected)
                const bulkVis = getBulkVisibility(field.value)

                function handleBulkChange(value: string) {
                  if (value === 'custom') return
                  field.onChange(
                    field.value.map((vt) => ({
                      ...vt,
                      visibilityOverride: vt.isSelected ? value : vt.visibilityOverride,
                    })),
                  )
                }

                // Gap 1: Inherit visibility from bulk row when checking
                function toggleCheckbox(idx: number, checked: boolean) {
                  const updated = [...field.value]
                  const current = updated[idx]
                  if (current) {
                    const inheritedVis =
                      checked ?
                        bulkVis === 'custom' ?
                          'all'
                        : bulkVis
                      : current.visibilityOverride
                    updated[idx] = { ...current, isSelected: checked, visibilityOverride: inheritedVis }
                  }
                  field.onChange(updated)
                }

                function updateVisibility(idx: number, value: string) {
                  const updated = [...field.value]
                  const current = updated[idx]
                  if (current) {
                    updated[idx] = { ...current, visibilityOverride: value }
                  }
                  field.onChange(updated)
                }

                return (
                  <div className='overflow-hidden rounded-lg border border-border-medium bg-white'>
                    {/* Section header */}
                    <div className='border-b border-border-medium px-sp-3 py-sp-2'>
                      <div className='flex items-center gap-sp-1'>
                        <Heading level='h3'>Booking access settings</Heading>
                        {hasSelected ?
                          <svg width='16' height='16' viewBox='0 0 16 16' fill='#2a7065'>
                            <path d='M13.3 4.3a1 1 0 0 1 0 1.4l-6 6a1 1 0 0 1-1.4 0l-3-3a1 1 0 1 1 1.4-1.4L6.5 9.6l5.3-5.3a1 1 0 0 1 1.4 0z' />
                          </svg>
                        : null}
                      </div>
                    </div>

                    <div className='px-sp-3 pb-sp-3 pt-sp-2'>
                      {errors.visitTypes && typeof errors.visitTypes.message === 'string' ?
                        <p className='mb-sp-2 text-sm text-text-error'>{errors.visitTypes.message}</p>
                      : null}

                      {/* "All services" header row */}
                      <div className='flex items-center justify-between border-b border-border-dark pb-sp-2'>
                        <span className='text-sm font-semibold tracking-[0.12px] text-text-dark'>All services</span>
                        <VisibilityButtonGroup value={bulkVis} onChange={handleBulkChange} showCustom />
                      </div>

                      {/* Service rows — scrollable */}
                      <div className='max-h-[400px] overflow-y-auto'>
                        {field.value.map((vt, idx) => (
                          <div
                            key={vt.visitTypeId}
                            className='flex items-center justify-between border-b border-border-dark py-sp-2 last:border-b-0'>
                            <div className='flex items-center gap-[12px]'>
                              <input
                                type='checkbox'
                                checked={vt.isSelected}
                                onChange={(e) => toggleCheckbox(idx, e.target.checked)}
                                className='size-6 shrink-0 rounded accent-[#178199]'
                              />
                              <div className='flex flex-col'>
                                <span className='text-sm font-semibold tracking-[0.12px] text-text-dark'>
                                  {vt.serviceName}
                                </span>
                                <span className='text-xs tracking-[0.12px] text-text-light'>{vt.visitName}</span>
                              </div>
                            </div>
                            <VisibilityButtonGroup
                              value={vt.visibilityOverride}
                              onChange={(val) => updateVisibility(idx, val)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className='flex justify-end gap-sp-1 border-t border-border-dark bg-white px-sp-3 py-sp-2'>
          <Button type='button' priority='secondary' onClick={handleNavigateBack}>
            Cancel
          </Button>
          <Button type='submit' loading={isPending} disabled={!isValid}>
            Save
          </Button>
        </div>
      </form>

      {/* Gap 6: Unsaved changes confirmation */}
      {showDiscardPopup ?
        <Popup
          isOpen
          header={{ title: 'Discard changes?' }}
          body='You have unsaved changes that will be lost.'
          footer={
            <div className='flex justify-end gap-sp-1'>
              <Button priority='secondary' onClick={() => setShowDiscardPopup(false)}>
                Cancel
              </Button>
              <Button priority='primary' onClick={navigateBack}>
                Discard
              </Button>
            </div>
          }
          onClose={() => setShowDiscardPopup(false)}
          showCloseIcon
        />
      : null}
    </div>
  )
}
